import React, {
  memo, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { DotsVertical } from '@picsio/icons';
import { useDispatch } from 'react-redux';
import { sendAssetsToTranscribe } from '../../../../store/actions/assets';
import TwoLevelMenu from '../../../TwoLevelMenu';
import menuControls, { download, suggestImprovement } from './controls';
import { showDialog } from '../../../dialog/index';
import CopyTranscriptDialog from '../CopyTranscriptDialog';
import l18n from '../../../../shared/strings';
import Tooltip from '../../../Tooltip';
import Logger from '../../../../services/Logger';

const TranscriptMenu = ({
  isDownloadable, downloadSub, assetId, lang = 'en', onDelete, transcript, onSuggestImprovement,
}) => {
  const dispatch = useDispatch();
  const settings = useMemo(() => ({ locale: lang }), [lang]);

  const handleChangeMenu = useCallback((menuId, itemId) => {
    /** if language selected */
    if (itemId !== null) {
      Logger.log('User', 'TranscriptTranscribeLangClicked');
      showDialog({
        title: l18n.TRANSCRIPT.DIALOG_OVERWRITE_EXISTING.TITLE,
        text: l18n.TRANSCRIPT.DIALOG_OVERWRITE_EXISTING.TEXT(),
        textBtnOk: l18n.TRANSCRIPT.DIALOG_OVERWRITE_EXISTING.BTN_OK,
        onOk() {
          Logger.log('User', 'OverwriteTranscriptConfirmed');
          dispatch(sendAssetsToTranscribe({ assetIds: [assetId], language: itemId }));
        },
        onCancel: () => Logger.log('User', 'OverwriteTranscriptModalClosed'),
      });
      Logger.log('User', 'OverwriteTranscriptModalShown');
    }

    if (menuId === 'menuCopySettings') {
      Logger.log('User', 'TranscriptCopySettingsClicked');
      CopyTranscriptDialog({
        transcript,
        isSettingsMode: true,
      });
    }

    if (menuId === 'menuDownload') {
      downloadSub();
    }

    if (menuId === suggestImprovement.id && onSuggestImprovement) {
      onSuggestImprovement();
    }

    if (menuId === 'menuDelete') {
      Logger.log('User', 'TranscriptDeleteTranscriptBtnClicked');

      showDialog({
        title: l18n.TRANSCRIPT.DIALOG_DELETE_TRANSCRIPT.TITLE,
        text: l18n.TRANSCRIPT.DIALOG_DELETE_TRANSCRIPT.TEXT(),
        textBtnOk: l18n.TRANSCRIPT.DIALOG_DELETE_TRANSCRIPT.BTN_OK,
        onOk: () => onDelete(),
        onCancel: () => Logger.log('User', 'TranscriptDeleteTranscriptModalClosed'),
      });
      Logger.log('User', 'TranscriptDeleteTranscriptModalShown');
    }
  }, [onSuggestImprovement, dispatch, assetId, transcript, downloadSub, onDelete]);

  const controls = useMemo(() => {
    const result = [...menuControls];

    if (isDownloadable) {
      result.unshift({ ...download });
    }
    if (onSuggestImprovement) {
      result.push({ ...suggestImprovement });
    }

    return result;
  }, [isDownloadable, onSuggestImprovement]);

  return (
    <Tooltip content={l18n.TRANSCRIPT.tooltipMoreActions} placement="top">
      <div>
        <TwoLevelMenu
          MainIcon={() => <DotsVertical size="lg" />}
          controls={controls}
          value={settings}
          onChange={handleChangeMenu}
          testId="TranscriptMenu"
          hideOnChange
        />
      </div>
    </Tooltip>
  );
};

TranscriptMenu.propTypes = {
  isDownloadable: PropTypes.bool.isRequired,
  downloadSub: PropTypes.func.isRequired,
  assetId: PropTypes.string.isRequired,
  lang: PropTypes.string,
  transcript: PropTypes.array.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSuggestImprovement: PropTypes.func,
};

export default memo(TranscriptMenu);
